import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  Switch,
  Button,
  message,
  Typography,
  Select,
} from "antd";
import {
  StockOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API, Headers } from "../../Config";

const { Text, Title } = Typography;
const { Option } = Select;

const UpdateStockModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedBoxNumbers, setSelectedBoxNumbers] = useState([]);
  const [isStock, setIsStock] = useState(true);
  const [boxOptions, setBoxOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Fetch default box numbers on component mount
  useEffect(() => {
    if (visible) {
      fetchBoxNumbers();
    }
  }, [visible]);

  // Fetch box numbers from API
  const fetchBoxNumbers = useCallback(async (search = "", limit = 10) => {
    try {
      setSearchLoading(true);
      const response = await axios.get(
        `${API.BASR_URL}boxesNew/findAllBoxesNumber`,
        {
          params: { search, limit },
          headers: Headers(),
        }
      );

      if (response.data.status) {
        const boxData = response.data.data || [];
        const options = boxData.map((box) => ({
          label: box.box_number,
          value: box.box_number,
        }));

        // Always replace options with the new search results
        setBoxOptions(options);
      }
    } catch (error) {
      console.error("Error fetching box numbers:", error);
      message.error("Failed to fetch box numbers");
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Handle search in Select component
  const handleSearch = useCallback(
    (value) => {
      // Always pass the search term to API, even if empty
      fetchBoxNumbers(value || "", 10);
    },
    [fetchBoxNumbers]
  );

  // Handle selection change
  const handleSelectionChange = (values) => {
    setSelectedBoxNumbers(values);
  };

  const handleSubmit = async () => {
    try {
      if (selectedBoxNumbers.length === 0) {
        message.error("Please select at least one box number");
        return;
      }

      setLoading(true);

      const requestData = {
        box_numbers: selectedBoxNumbers,
        is_stock: isStock,
      };

      console.log("Sending request:", requestData);

      const response = await axios.put(
        `${API.BASR_URL}${API.BOXES_UPDATE_STOCK_STATUS}`,
        requestData,
        { headers: Headers() }
      );

      if (response.status === 201 || response.status === 200) {
        const result = response.data;

        if (result.status) {
          message.success(
            result.message || "Stock status updated successfully"
          );

          // Reset form
          form.resetFields();
          setSelectedBoxNumbers([]);
          setIsStock(true);

          // Close modal and trigger success callback
          onCancel();
          if (onSuccess) {
            onSuccess();
          }
        } else {
          message.error(result.message || "Failed to update stock status");

          // Show not found box numbers if any
          if (result.data && result.data.notFoundBoxNumbers) {
            message.warning(
              `Box numbers not found: ${result.data.notFoundBoxNumbers.join(
                ", "
              )}`
            );
          }
        }
      } else {
        message.error("Failed to update stock status");
      }
    } catch (error) {
      console.error("Error updating stock status:", error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.data && errorData.data.notFoundBoxNumbers) {
          message.error(
            `Some box numbers not found: ${errorData.data.notFoundBoxNumbers.join(
              ", "
            )}`
          );
        } else {
          message.error(errorData.message || "Failed to update stock status");
        }
      } else {
        message.error("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    form.resetFields();
    setSelectedBoxNumbers([]);
    setIsStock(true);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <StockOutlined style={{ color: "white", fontSize: "20px" }} />
          <Title level={4} style={{ margin: 0, color: "white" }}>
            Update Stock Status
          </Title>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* Box Numbers Selection */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ marginBottom: "12px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
              }}
            >
              <SearchOutlined style={{ color: "#1e88e5", fontSize: "16px" }} />
              <Text strong style={{ fontSize: "14px", color: "#262626" }}>
                Select Box Numbers
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Search and select multiple box numbers to update
            </Text>
          </div>

          <Form.Item
            name="boxNumbers"
            rules={[
              {
                required: true,
                message: "Please select at least one box number",
              },
            ]}
            style={{ marginBottom: "8px" }}
          >
            <Select
              mode="multiple"
              placeholder="Search and select box numbers..."
              value={selectedBoxNumbers}
              onChange={handleSelectionChange}
              onSearch={handleSearch}
              loading={searchLoading}
              showSearch
              allowClear
              style={{ width: "100%" }}
              filterOption={false}
              notFoundContent={
                searchLoading ? (
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      color: "#8c8c8c",
                    }}
                  >
                    Loading...
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      color: "#8c8c8c",
                    }}
                  >
                    No box numbers found
                  </div>
                )
              }
            >
              {boxOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedBoxNumbers.length > 0 && (
            <div style={{ marginTop: "8px" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Selected: {selectedBoxNumbers.length} box
                {selectedBoxNumbers.length !== 1 ? "es" : ""}
              </Text>
            </div>
          )}
        </div>

        {/* Stock Status */}
        <div style={{ marginBottom: "32px" }}>
          <div style={{ marginBottom: "12px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
              }}
            >
              <StockOutlined style={{ color: "#1e88e5", fontSize: "16px" }} />
              <Text strong style={{ fontSize: "14px", color: "#262626" }}>
                Stock Status
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Set availability status for selected boxes
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px",
              backgroundColor: "#fafafa",
              borderRadius: "6px",
              border: "1px solid #f0f0f0",
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                {isStock ? (
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", fontSize: "16px" }}
                  />
                ) : (
                  <CloseCircleOutlined
                    style={{ color: "#ff4d4f", fontSize: "16px" }}
                  />
                )}
                <Text strong style={{ fontSize: "14px", color: "#262626" }}>
                  {isStock ? "In Stock" : "Out of Stock"}
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {isStock
                  ? "Boxes will be marked as available in inventory"
                  : "Boxes will be marked as unavailable in inventory"}
              </Text>
            </div>

            <Switch checked={isStock} onChange={setIsStock} size="default" />
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            paddingTop: "16px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            onClick={handleCancel}
            disabled={loading}
            style={{
              height: "36px",
              padding: "0 16px",
              borderRadius: "6px",
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={selectedBoxNumbers.length === 0}
          >
            {loading ? "Updating..." : "Update Stock Status"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default UpdateStockModal;
